import { useCallback, useRef, useState } from 'react';
import { createDocument, ToolContentDocument } from '@easytools/tool-content';

const LIMIT = 100;
// Modifiche dello stesso campo entro questo tempo diventano un solo passo da annullare.
const COALESCE_MS = 1200;

type Update = ToolContentDocument | ((current: ToolContentDocument) => ToolContentDocument);

/**
 * Il documento in modifica con la sua cronologia: ogni cambiamento si può annullare e
 * ripetere. Chi scrive in un campo del pannello passa una chiave (`coalesce`) così una
 * parola non diventa dieci passi.
 */
export function useDocHistory() {
    const [doc, setState] = useState<ToolContentDocument>(() => createDocument());
    const current = useRef(doc);
    const past = useRef<ToolContentDocument[]>([]);
    const future = useRef<ToolContentDocument[]>([]);
    const last = useRef<{ key: string; at: number } | null>(null);
    const [, setVersion] = useState(0);
    const bump = () => setVersion(v => v + 1);

    const apply = (next: ToolContentDocument) => {
        current.current = next;
        setState(next);
    };

    /** Cambia il documento e lo registra nella cronologia. */
    const setDoc = useCallback((update: Update, coalesce?: string) => {
        const before = current.current;
        const next = typeof update === 'function' ? update(before) : update;
        if (next === before) return;
        const now = Date.now();
        const merge = coalesce && last.current?.key === coalesce && now - last.current.at < COALESCE_MS;
        if (!merge) {
            past.current = [...past.current, before].slice(-LIMIT);
        }
        last.current = coalesce ? { key: coalesce, at: now } : null;
        future.current = [];
        apply(next);
        bump();
    }, []);

    /** Sostituisce il documento senza cronologia (caricamento di un contenuto). */
    const resetDoc = useCallback((next: ToolContentDocument) => {
        past.current = [];
        future.current = [];
        last.current = null;
        apply(next);
        bump();
    }, []);

    /** Sostituisce il documento corrente lasciando com'è la cronologia. */
    const replaceDoc = useCallback((next: ToolContentDocument) => {
        apply(next);
        bump();
    }, []);

    /** Mette un documento precedente in cronologia, per annullare ciò che è arrivato da fuori (una generazione). */
    const pushPast = useCallback((previous: ToolContentDocument) => {
        past.current = [...past.current, previous].slice(-LIMIT);
        future.current = [];
        last.current = null;
        bump();
    }, []);

    const undo = useCallback(() => {
        const previous = past.current[past.current.length - 1];
        if (!previous) return false;
        past.current = past.current.slice(0, -1);
        future.current = [current.current, ...future.current];
        last.current = null;
        apply(previous);
        bump();
        return true;
    }, []);

    const redo = useCallback(() => {
        const next = future.current[0];
        if (!next) return false;
        future.current = future.current.slice(1);
        past.current = [...past.current, current.current];
        last.current = null;
        apply(next);
        bump();
        return true;
    }, []);

    return {
        doc,
        setDoc,
        resetDoc,
        replaceDoc,
        pushPast,
        undo,
        redo,
        canUndo: past.current.length > 0,
        canRedo: future.current.length > 0,
    };
}
